/**
 * Per-deck card loading from LOCAL storage — the "on disk" residency tier.
 *
 * Under lazy card hydration (`lazyCardHydration` capability) hydrate leaves
 * card bodies on disk; a deck's rows enter memory through this loader: the hot
 * set right after first paint (`flashka-store.ts` stage 1.5), and any deck the
 * user opens (`use-hydrate-cold-decks.ts`, `rehydrateColdDeck`, the SG-mirror
 * disk hook). It is also what makes a deck open OFFLINE.
 *
 * Contract:
 *   - reads `getByIndex('cards','deckId',id)` (awaits the storage flush, so a
 *     row a frame just queued is included) and normalises through the SAME
 *     helper hydrate uses (`card-rows.ts`) — identical rows either way;
 *   - applies under `withServerSource`: the rows came FROM storage and the
 *     dirty scan must not see them as a local edit (a "local" tick with N new
 *     card refs would push the whole deck);
 *   - memory wins on a shared id; the deck is always marked disk-loaded (every
 *     local row is in memory, even when disk had nothing — what the disk hook
 *     and the reconnect flush read), marked loaded only when the merged bucket is complete
 *     (`applyDeckCardsFromDisk`), and the promise resolves to
 *     `deckCardsResident` afterwards — `false` tells the caller to ask the
 *     server;
 *   - concurrent callers for one deck share a single read;
 *   - never bumps server-side activity: preloading must not keep a deck warm.
 *
 * The store is injected once by `flashka-store.ts` (`registerDiskLoadStore`)
 * instead of imported, so this module has no import cycle with the store and
 * hydrate can use it directly.
 */
import { getSyncStorage, isSyncStorageRegistered } from '../provider/sync-storage';
import { deckCardsResident } from './deck-residency';
import { withServerSource } from './framework/mutation-source';
import { perfNow } from './perf-log';
import { persistRepairedRows, readDeckCardRowsFromDisk } from './store/card-rows';
import type { FlashkaState } from './store/flashka-store';
import { reportSyncStorageError } from './store/store-utils';

export type DiskLoadStore = {
  getState: () => FlashkaState;
  subscribe: (listener: (state: FlashkaState, prev: FlashkaState) => void) => () => void;
};

/**
 * Disk-first policy: may this deck's bodies be served from local storage
 * WITHOUT asking the server first?
 *
 *   - a deck the server keeps warm (not archived): yes. The sync-diff and the
 *     live frames keep this device's rows the full current set, so the read
 *     is exact, works offline, and costs no `request-deck-cards` round trip.
 *   - an archived (cold) deck: only when `allowArchived` — the OFFLINE case.
 *     A deck cold on the server since before this device's first sync has no
 *     or a partial local set, and the server round-trip on open is what makes
 *     it fresh; offline, a stale set beats an empty screen, and the next
 *     online open heals it (the activity bump re-warms the deck, the diff
 *     brings the rest).
 *
 * Study-group mirrors are ordinary decks here: their rows live on this
 * device like any other deck's, and UserDo serves `request-deck-cards` for
 * them too.
 */
export function diskFirstAllowed(
  state: Pick<FlashkaState, 'archivedDeckIds'>,
  deckId: string,
  allowArchived: boolean,
): boolean {
  return allowArchived || !state.archivedDeckIds.has(deckId);
}

let registeredStore: DiskLoadStore | null = null;
let unsubscribeStore: (() => void) | null = null;

/**
 * Called once by the store module; specs may point it at a double. Also
 * watches for `reset()` (`isHydrated` true → false): every read in flight then
 * belongs to the user who just left, so the shared-promise map is cleared —
 * the next user's first call for the same deck starts its own read instead of
 * being handed the old one (each stale read still drops its result, see
 * `loadDeckCardsFromDisk`).
 */
export function registerDiskLoadStore(store: DiskLoadStore): void {
  registeredStore = store;
  unsubscribeStore?.();
  unsubscribeStore = store.subscribe((state, prev) => {
    if (prev.isHydrated && !state.isHydrated) inflight.clear();
  });
}

function requireStore(): DiskLoadStore {
  if (!registeredStore) {
    throw new Error('deck-disk-load: no store registered (import the sync store first)');
  }
  return registeredStore;
}

const inflight = new Map<string, Promise<boolean>>();

/**
 * Load one deck's rows from local storage into the store. Resolves `true` when
 * the deck's bodies are resident afterwards (fully in memory), `false` when
 * disk had nothing or only part of the set — the caller decides whether the
 * server is the next stop.
 *
 * The apply runs under the server source, so the dirty scan does not read the
 * loaded rows as local edits; and should a local tick's flush be pending when
 * they land, `detectDirty` still skips them — rows persisted from server
 * frames carry their `__card` timestamp entries, and the only rows without one
 * are unsynced local edits, which must push anyway.
 */
export function loadDeckCardsFromDisk(deckId: string): Promise<boolean> {
  const pending = inflight.get(deckId);
  if (pending) return pending;
  const run = (async (): Promise<boolean> => {
    const store = requireStore();
    const before = store.getState();
    if (deckCardsResident(before, deckId)) return true;
    // No storage yet means nothing on disk to read, not a failure. The deck
    // page renders before an account is known — a session check that got no
    // answer, offline — and its cold-deck pass must not throw "No storage
    // implementation registered" into the console on every attempt.
    if (!isSyncStorageRegistered()) return false;
    // The read spans an await: a sign-out and the NEXT user's hydrate can both
    // complete before it resolves, and `isHydrated` alone would then wave the
    // previous user's rows into the new user's store. The user id pins it.
    const userId = before.currentUserId;
    const storage = getSyncStorage();
    const { map, repaired } = await readDeckCardRowsFromDisk(storage, deckId);
    const state = store.getState();
    if (!state.isHydrated || state.currentUserId !== userId) return false;
    withServerSource(() => state.applyDeckCardsFromDisk(deckId, map));
    if (repaired.length > 0) {
      try {
        await persistRepairedRows(storage, repaired);
      } catch (err) {
        reportSyncStorageError(err, 'deck-disk-load-repair');
      }
    }
    return deckCardsResident(store.getState(), deckId);
  })();
  const tracked = run.finally(() => {
    if (inflight.get(deckId) === tracked) inflight.delete(deckId);
  });
  inflight.set(deckId, tracked);
  return tracked;
}

/** One macrotask, so a burst of loads leaves room for a frame between decks. */
export const yieldToEventLoop = (): Promise<void> =>
  new Promise((resolve) => setTimeout(resolve, 0));

/**
 * Load several decks one after another, yielding between them, stopping early
 * when `shouldContinue` says the work is no longer wanted (user switched).
 * Returns the ids that are resident afterwards.
 */
export async function loadDecksFromDisk(
  deckIds: readonly string[],
  shouldContinue: () => boolean = () => true,
  /**
   * Time spent INSIDE each deck's load, in ms — the pass's own cost, separated
   * from the wall clock it reports. The two are wildly different here and the
   * difference is the whole point: this loop yields a macrotask between decks,
   * so on a busy cold start most of its elapsed time is OTHER work running, not
   * this work being slow. Without the split a 10s wall clock reads as a 10s
   * stall and invites "fixing" a pass that is already cooperative.
   */
  onDeckLoaded?: (deckId: string, selfMs: number, resident: boolean) => void,
): Promise<string[]> {
  const resident: string[] = [];
  for (const id of deckIds) {
    if (!shouldContinue()) break;
    const started = perfNow();
    const ok = await loadDeckCardsFromDisk(id);
    onDeckLoaded?.(id, perfNow() - started, ok);
    if (ok) resident.push(id);
    await yieldToEventLoop();
  }
  return resident;
}
